import {
  Box,
  BoxProps,
  forwardRef,
  HStack,
  Input,
  Tag,
  TagCloseButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { TagVariant } from '~/components/defaults/componentStyles/Tag.style'
import { useTagInputStyles } from '~/components/shared/componentStyles/TagInput.style'
import { tryGetValueOrDefault } from '~/utils/common'

type TagInputProps = BoxProps & {
  placeholder?: string
  value?: string
  allowMultiple?: boolean
  guideText?: string
  evalOnBlur?: boolean
  maxTags?: number
  valueType?: string
  initialTags?: string[]
  onInputValueChange?: (value: string) => void
  valueValidator?: (value: string) => boolean
  onTagsUpdated?: (tags: string[]) => void
}

export const TagInput = forwardRef<TagInputProps, 'div'>(
  (props: TagInputProps, ref) => {
    const [tags, setTags] = useState<string[]>([])
    const [inputValue, setInputValue] = useState<string>('')
    const [inputErrors, setInputErrors] = useState<string[]>()
    const inputRef = useRef<HTMLInputElement>(null)
    const styles = useTagInputStyles()

    const {
      placeholder,
      value: externalValue = '',
      allowMultiple = true,
      guideText = true,
      evalOnBlur = true,
      maxTags = 5,
      valueType,
      initialTags = [],
      onInputValueChange,
      valueValidator,
      onTagsUpdated,
      ...rest
    } = props

    const updateTagState = (newTags: string[]) => {
      setTags(newTags)
      onTagsUpdated?.(newTags)
    }

    const handleAddNewTags = (newTags: string[]) => {
      const errorValues: string[] = []
      const filteredTags = newTags
        .map((tag) => tag.trim())
        .filter((tag) => {
          const isValid = valueValidator ? valueValidator(tag) : true

          if (!isValid) {
            errorValues.push(tag)
          }

          return isValid && !tags.includes(tag)
        })
      const mergedTags = [...tags, ...filteredTags].slice(0, maxTags)
      updateTagState(mergedTags)
      return errorValues
    }

    useEffect(() => {
      handleAddNewTags(initialTags)
    }, [])

    const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault()
      const value = inputRef.current?.value
      if (!value) return
      const newTags = value.split(',')
      const errors = handleAddNewTags(newTags)
      if (errors.length > 0) {
        setInputErrors(errors)
      }

      inputRef.current!.value = errors.join(',')
      setInputValue('')
    }

    const handleFocusLost = () => {
      if (!evalOnBlur) return

      handleSubmit()
    }

    const handleDeleteTag = (index: number) => () => {
      const newTags = [...tags]
      newTags.splice(index, 1)
      updateTagState(newTags)
    }

    const handleResetError = () => {
      if (inputErrors) {
        setInputErrors(undefined)
      }
    }

    const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      onInputValueChange?.(e.target.value)
      handleResetError()
    }

    const getErrorMessage = () => {
      const isPlural = inputErrors!.length > 1

      const subject = isPlural ? `These` : `This`
      const valuesText = isPlural ? 'values' : 'value'
      if (!valueType) {
        return `${subject} ${valuesText} could not be added`
      }

      const isAre = isPlural ? 'are' : 'is'
      return `${subject} ${valuesText} ${isAre} not a valid ${valueType}`
    }

    const tagJsx = useMemo(() => {
      return tags.map((tag, index) => {
        return (
          <Tag key={index} variant={TagVariant.TagInput}>
            <Text fontWeight={500}>{tag}</Text>
            <TagCloseButton
              sx={styles.tagCloseButton}
              onClick={handleDeleteTag(index)}
            />
          </Tag>
        )
      })
    }, [tags])

    const inputDisabled = tags.length >= maxTags
    return (
      <Box as={VStack} sx={styles.dataContainer} ref={ref} {...rest}>
        <HStack sx={styles.tagContainer}>{tagJsx}</HStack>
        <VStack sx={styles.inputContainer}>
          <form onSubmit={handleSubmit}>
            <Input
              ref={inputRef}
              onBlur={handleFocusLost}
              placeholder={inputDisabled ? undefined : placeholder}
              value={
                inputDisabled
                  ? ''
                  : tryGetValueOrDefault(externalValue, inputValue)
              }
              disabled={inputDisabled}
              onChange={handleInputChanged}
            />
          </form>
          {!inputErrors && guideText && (
            <Text alignSelf="flex-start" sx={styles.guideText}>
              {guideText}
            </Text>
          )}
          {inputErrors && (
            <VStack sx={styles.errorContainer}>
              <Text sx={styles.errorText}>{getErrorMessage()}</Text>
              {allowMultiple && (
                <Text sx={styles.errorText}>
                  Multiple values must be comma separated
                </Text>
              )}
            </VStack>
          )}
        </VStack>
      </Box>
    )
  },
)
